// Chemin: C:\gestion-copro\src\services\pdfSentinelle.service.ts
// Session 181 : Export PDF des résultats structurés SENTINELLE
// Génère un PDF professionnel à partir d'un message SENTINELLE contenant des tableaux/calculs
// Utilise jsPDF — cohérent avec pdfCourrier.service.ts (pas d'emojis Unicode)

import jsPDF from 'jspdf';

// ============================================================================
// CONFIGURATION — alignée sur pdfCourrier.service.ts
// ============================================================================

const CONFIG = {
  marginLeft: 20,
  marginRight: 20,
  marginTop: 20,
  marginBottom: 25,
  pageWidth: 210,
  pageHeight: 297,
  fontSizeNormal: 10,
  fontSizeSmall: 8,
  fontSizeLarge: 11,
  fontSizeTitle: 14,
  fontSizeH2: 11,
  fontSizeH3: 10,
  lineHeight: 5,
  paragraphSpacing: 6,
  tableRowHeight: 6,
  tablePadding: 3,
};

const COLORS = {
  primary: [180, 120, 20] as [number, number, number],       // Ambre Copro Protect
  headerBg: [245, 240, 230] as [number, number, number],     // Fond en-tête tableau
  tableBorder: [200, 200, 200] as [number, number, number],  // Bordures tableau
  altRowBg: [250, 250, 250] as [number, number, number],     // Lignes alternées
  textDark: [30, 30, 30] as [number, number, number],
  textMedium: [100, 100, 100] as [number, number, number],
  textLight: [150, 150, 150] as [number, number, number],
  warning: [180, 120, 20] as [number, number, number],
  success: [30, 130, 80] as [number, number, number],
  error: [180, 50, 50] as [number, number, number],
};

// ============================================================================
// INTERFACES
// ============================================================================

interface PDFSentinelleOptions {
  /** Contenu markdown du message SENTINELLE */
  contenu: string;
  /** Nom de la copropriété */
  nomCopropriete: string;
  /** Nom de l'utilisateur */
  nomUtilisateur: string;
  /** Date du message */
  dateMessage: Date;
  /** Titre optionnel (sinon extrait du contenu) */
  titre?: string;
}

/** Bloc parsé du contenu markdown */
type ParsedBlock =
  | { type: 'h2'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'bullet'; items: string[] }
  | { type: 'numbered'; items: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'callout'; style: 'warning' | 'success' | 'error' | 'info'; text: string }
  | { type: 'separator' };

// ============================================================================
// NETTOYAGE TEXTE — identique à pdfCourrier.service.ts
// ============================================================================

function cleanText(text: string): string {
  if (!text) return '';
  return text
    // Supprimer emojis Unicode (problèmes jsPDF)
    .replace(/[\u{1F600}-\u{1F64F}]/gu, '')
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')
    .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '')
    .replace(/[\u{2600}-\u{26FF}]/gu, '')
    .replace(/[\u{2700}-\u{27BF}]/gu, '')
    // Supprimer emojis courants qui passent à travers (⚠️, ✅, ❌, 💡, ℹ️)
    .replace(/[\u{FE00}-\u{FE0F}]/gu, '') // variation selectors
    .replace(/[\u{200D}]/gu, '') // zero-width joiner
    .replace(/[\u{20E3}]/gu, '') // combining enclosing keycap
    .replace(/[\u00FE]/g, '') // þ résidu d'emoji mal décodé
    .replace(/[\u{2705}\u{2714}\u{2716}\u{2757}\u{2753}\u{2755}\u{26A0}\u{2139}\u{274C}\u{274E}\u{2B55}\u{1F4A1}]/gu, '')
    // Remplacer caractères typographiques
    .replace(/["\u201C\u201D]/g, '"')
    .replace(/['\u2018\u2019]/g, "'")
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
    // Nettoyer marqueurs markdown inline
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();
}

/** Supprime le markdown inline pour le texte brut PDF */
function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
}

// ============================================================================
// PARSEUR MARKDOWN → BLOCS STRUCTURÉS
// ============================================================================

function isTableSeparator(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.startsWith('|')) return false;
  const inner = trimmed.replace(/\|/g, '').replace(/[\s:-]/g, '');
  return inner === '' && trimmed.includes('-');
}

function isTableRow(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.includes('|', 1);
}

function parseTableCells(line: string): string[] {
  const trimmed = line.trim();
  const inner = trimmed.startsWith('|') ? trimmed.slice(1) : trimmed;
  const cleaned = inner.endsWith('|') ? inner.slice(0, -1) : inner;
  return cleaned.split('|').map(cell => stripMarkdown(cell.trim()));
}

function detectCalloutStyle(text: string): 'warning' | 'success' | 'error' | 'info' | null {
  const lower = text.toLowerCase();
  if (lower.includes('attention') || lower.includes('avertissement') || lower.includes('calcul incomplet')) return 'warning';
  if (lower.includes('recuperable') || lower.includes('complet')) return 'success';
  if (lower.includes('non recuperable') || lower.includes('erreur')) return 'error';
  if (lower.includes('conseil') || lower.includes('note') || lower.includes('information')) return 'info';
  return null;
}

function parseMarkdownToBlocks(content: string): ParsedBlock[] {
  const normalized = content.replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  const lines = normalized.split('\n');
  const blocks: ParsedBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Ligne vide
    if (trimmed === '') { i++; continue; }

    // Séparateur
    if (/^-{3,}$/.test(trimmed) || /^\*{3,}$/.test(trimmed)) {
      blocks.push({ type: 'separator' });
      i++;
      continue;
    }

    // H2
    if (trimmed.startsWith('## ')) {
      blocks.push({ type: 'h2', text: stripMarkdown(trimmed.slice(3)) });
      i++;
      continue;
    }

    // H3
    if (trimmed.startsWith('### ')) {
      blocks.push({ type: 'h3', text: stripMarkdown(trimmed.slice(4)) });
      i++;
      continue;
    }

    // Tableau
    if (isTableRow(trimmed)) {
      const tableRows: string[] = [];
      let hasHeader = false;
      while (i < lines.length && (isTableRow(lines[i].trim()) || isTableSeparator(lines[i].trim()))) {
        if (isTableSeparator(lines[i].trim())) {
          hasHeader = true;
        } else {
          tableRows.push(lines[i]);
        }
        i++;
      }
      if (tableRows.length > 0) {
        const headers = hasHeader ? parseTableCells(tableRows[0]) : [];
        const bodyRows = hasHeader ? tableRows.slice(1) : tableRows;
        blocks.push({
          type: 'table',
          headers,
          rows: bodyRows.map(r => parseTableCells(r)),
        });
      }
      continue;
    }

    // Liste à puces
    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(stripMarkdown(lines[i].trim().replace(/^[-*]\s+/, '')));
        i++;
      }
      blocks.push({ type: 'bullet', items });
      continue;
    }

    // Liste numérotée
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(stripMarkdown(lines[i].trim().replace(/^\d+\.\s+/, '')));
        i++;
      }
      blocks.push({ type: 'numbered', items });
      continue;
    }

    // Blockquote / encadré
    if (trimmed.startsWith('> ')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('> ')) {
        quoteLines.push(lines[i].trim().slice(2));
        i++;
      }
      const fullText = stripMarkdown(quoteLines.join(' '));
      const style = detectCalloutStyle(fullText);
      if (style) {
        blocks.push({ type: 'callout', style, text: fullText });
      } else {
        blocks.push({ type: 'callout', style: 'info', text: fullText });
      }
      continue;
    }

    // Paragraphe
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].trim().startsWith('## ') &&
      !lines[i].trim().startsWith('### ') &&
      !/^[-*]\s+/.test(lines[i].trim()) &&
      !/^\d+\.\s+/.test(lines[i].trim()) &&
      !/^-{3,}$/.test(lines[i].trim()) &&
      !lines[i].trim().startsWith('> ') &&
      !isTableRow(lines[i].trim())
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }
    if (paraLines.length > 0) {
      blocks.push({ type: 'paragraph', text: stripMarkdown(paraLines.join(' ')) });
    }
  }

  return blocks;
}

// ============================================================================
// DÉTECTION RÉSULTAT STRUCTURÉ
// ============================================================================

/**
 * Détermine si un message SENTINELLE contient un résultat structuré exportable en PDF.
 * Critère strict : présence d'au moins un tableau markdown COMPLET (header + séparateur + données).
 * Les messages conversationnels, questions de clarification et listes simples ne déclenchent PAS le bouton.
 */
export function isResultatStructure(content: string): boolean {
  if (!content) return false;

  const lines = content.split('\n');
  
  // Chercher un tableau markdown complet : header | séparateur | au moins 1 ligne de données
  let tableState: 'none' | 'header' | 'separator' = 'none';
  let dataRowCount = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (tableState === 'none' && isTableRow(trimmed) && !isTableSeparator(trimmed)) {
      tableState = 'header';
    } else if (tableState === 'header' && isTableSeparator(trimmed)) {
      tableState = 'separator';
    } else if (tableState === 'separator' && isTableRow(trimmed) && !isTableSeparator(trimmed)) {
      dataRowCount++;
      if (dataRowCount >= 2) return true; // Tableau avec au moins 2 lignes de données
    } else if (tableState !== 'none' && trimmed === '') {
      // Ligne vide après début de tableau — on tolère
    } else if (tableState === 'separator') {
      // Ligne non-tableau après le séparateur — le tableau est fini
      if (dataRowCount >= 2) return true;
      tableState = 'none';
      dataRowCount = 0;
    } else {
      tableState = 'none';
      dataRowCount = 0;
    }
  }
  
  // Vérifier le dernier tableau en cours
  if (tableState === 'separator' && dataRowCount >= 2) return true;

  return false;
}

/**
 * Extrait un titre depuis le contenu (premier H2 ou premiers mots significatifs)
 */
function extraireTitre(content: string): string {
  const lines = content.split('\n');
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('## ')) {
      return stripMarkdown(trimmed.slice(3));
    }
  }
  // Fallback : premiers 60 caractères du premier paragraphe non vide
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed && !trimmed.startsWith('#') && !trimmed.startsWith('|') && !trimmed.startsWith('>')) {
      const clean = stripMarkdown(trimmed);
      return clean.length > 60 ? clean.slice(0, 57) + '...' : clean;
    }
  }
  return 'Analyse SENTINELLE';
}

// ============================================================================
// GÉNÉRATION PDF
// ============================================================================

function checkPageBreak(doc: jsPDF, yPos: number, needed: number): number {
  if (yPos + needed > CONFIG.pageHeight - CONFIG.marginBottom) {
    doc.addPage();
    return CONFIG.marginTop + 15; // Espace pour le mini-header de page suivante
  }
  return yPos;
}

export function genererPDFSentinelle(options: PDFSentinelleOptions): Blob {
  const { contenu, nomCopropriete, nomUtilisateur, dateMessage, titre } = options;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const contentWidth = CONFIG.pageWidth - CONFIG.marginLeft - CONFIG.marginRight;

  const blocks = parseMarkdownToBlocks(contenu);
  const titrePDF = titre || extraireTitre(contenu);
  const dateStr = dateMessage.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  let yPos = CONFIG.marginTop;

  // ================================================================
  // EN-TÊTE PREMIÈRE PAGE
  // ================================================================

  // Bandeau ambre
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, CONFIG.pageWidth, 3, 'F');

  // COPRO PROTECT
  doc.setFontSize(CONFIG.fontSizeTitle);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('COPRO PROTECT', CONFIG.marginLeft, yPos);

  // Analyse SENTINELLE — aligné à droite
  doc.setFontSize(CONFIG.fontSizeSmall);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.textMedium);
  const labelSentinelle = 'Analyse SENTINELLE';
  const labelWidth = doc.getTextWidth(labelSentinelle);
  doc.text(labelSentinelle, CONFIG.pageWidth - CONFIG.marginRight - labelWidth, yPos);
  yPos += 8;

  // Ligne de séparation
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.line(CONFIG.marginLeft, yPos, CONFIG.pageWidth - CONFIG.marginRight, yPos);
  yPos += 6;

  // Métadonnées : copropriété, utilisateur, date
  doc.setFontSize(CONFIG.fontSizeSmall);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.textMedium);
  doc.text(cleanText(nomCopropriete), CONFIG.marginLeft, yPos);
  const dateWidth = doc.getTextWidth(dateStr);
  doc.text(dateStr, CONFIG.pageWidth - CONFIG.marginRight - dateWidth, yPos);
  yPos += CONFIG.lineHeight;
  doc.text(cleanText(nomUtilisateur), CONFIG.marginLeft, yPos);
  yPos += 8;

  // Titre du document
  doc.setFontSize(CONFIG.fontSizeLarge);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.textDark);
  const titreLines = doc.splitTextToSize(cleanText(titrePDF), contentWidth);
  for (const tl of titreLines) {
    yPos = checkPageBreak(doc, yPos, CONFIG.lineHeight);
    doc.text(tl, CONFIG.marginLeft, yPos);
    yPos += CONFIG.lineHeight + 1;
  }
  yPos += CONFIG.paragraphSpacing;

  // ================================================================
  // RENDU DES BLOCS
  // ================================================================

  const titrePDFClean = cleanText(titrePDF).toUpperCase();
  let firstH2Skipped = false;

  for (const block of blocks) {
    switch (block.type) {

      case 'h2': {
        // Éviter le doublon : si le premier H2 est identique au titre en en-tête, on le saute
        if (!firstH2Skipped && cleanText(block.text).toUpperCase() === titrePDFClean) {
          firstH2Skipped = true;
          break;
        }
        firstH2Skipped = true;
        yPos = checkPageBreak(doc, yPos, 12);
        yPos += 4;
        doc.setFontSize(CONFIG.fontSizeH2);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.primary);
        doc.text(cleanText(block.text).toUpperCase(), CONFIG.marginLeft, yPos);
        yPos += CONFIG.lineHeight + 2;
        // Petite ligne sous le H2
        doc.setDrawColor(...COLORS.primary);
        doc.setLineWidth(0.3);
        doc.line(CONFIG.marginLeft, yPos, CONFIG.marginLeft + 40, yPos);
        yPos += 3;
        break;
      }

      case 'h3': {
        yPos = checkPageBreak(doc, yPos, 10);
        yPos += 2;
        doc.setFontSize(CONFIG.fontSizeH3);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.textDark);
        doc.text(cleanText(block.text), CONFIG.marginLeft, yPos);
        yPos += CONFIG.lineHeight + 1;
        break;
      }

      case 'paragraph': {
        const textLines = doc.splitTextToSize(cleanText(block.text), contentWidth);
        doc.setFontSize(CONFIG.fontSizeNormal);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.textDark);
        for (const tl of textLines) {
          yPos = checkPageBreak(doc, yPos, CONFIG.lineHeight);
          doc.text(tl, CONFIG.marginLeft, yPos);
          yPos += CONFIG.lineHeight;
        }
        yPos += CONFIG.paragraphSpacing / 2;
        break;
      }

      case 'bullet': {
        doc.setFontSize(CONFIG.fontSizeNormal);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...COLORS.textDark);
        for (const item of block.items) {
          const itemLines = doc.splitTextToSize(cleanText(item), contentWidth - 6);
          yPos = checkPageBreak(doc, yPos, itemLines.length * CONFIG.lineHeight);
          // Puce ronde
          doc.setFillColor(...COLORS.textMedium);
          doc.circle(CONFIG.marginLeft + 1.5, yPos - 1.2, 0.8, 'F');
          for (let li = 0; li < itemLines.length; li++) {
            doc.text(itemLines[li], CONFIG.marginLeft + 6, yPos);
            yPos += CONFIG.lineHeight;
          }
        }
        yPos += CONFIG.paragraphSpacing / 2;
        break;
      }

      case 'numbered': {
        doc.setFontSize(CONFIG.fontSizeNormal);
        doc.setTextColor(...COLORS.textDark);
        for (let idx = 0; idx < block.items.length; idx++) {
          const item = block.items[idx];
          const itemLines = doc.splitTextToSize(cleanText(item), contentWidth - 8);
          yPos = checkPageBreak(doc, yPos, itemLines.length * CONFIG.lineHeight);
          doc.setFont('helvetica', 'bold');
          doc.text(`${idx + 1}.`, CONFIG.marginLeft, yPos);
          doc.setFont('helvetica', 'normal');
          for (let li = 0; li < itemLines.length; li++) {
            doc.text(itemLines[li], CONFIG.marginLeft + 8, yPos);
            yPos += CONFIG.lineHeight;
          }
        }
        yPos += CONFIG.paragraphSpacing / 2;
        break;
      }

      case 'table': {
        const { headers, rows } = block;
        const numCols = headers.length > 0 ? headers.length : (rows[0]?.length || 1);

        // Tableaux larges (5+ colonnes) : police plus petite pour éviter la troncature
        const isWideTable = numCols >= 5;
        const tableFontSize = isWideTable ? 7 : CONFIG.fontSizeSmall;
        const tableRowH = isWideTable ? 5 : CONFIG.tableRowHeight;
        const tablePad = isWideTable ? 2 : CONFIG.tablePadding;

        // Calculer les largeurs de colonnes proportionnelles au contenu
        const colWidths = calculateColumnWidths(doc, headers, rows, contentWidth, numCols, tableFontSize);

        // Espace nécessaire : header + 3 premières lignes minimum
        const minTableHeight = tableRowH * Math.min(rows.length + 1, 4);
        yPos = checkPageBreak(doc, yPos, minTableHeight);
        yPos += 2;

        let xPos = CONFIG.marginLeft;

        // En-tête du tableau
        if (headers.length > 0) {
          doc.setFillColor(...COLORS.headerBg);
          doc.rect(CONFIG.marginLeft, yPos - 4, contentWidth, tableRowH + 1, 'F');
          doc.setFontSize(tableFontSize);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...COLORS.textDark);

          xPos = CONFIG.marginLeft;
          for (let c = 0; c < numCols; c++) {
            const cellText = headers[c] || '';
            // Wrapping multi-lignes pour les en-têtes larges
            const cellLines = doc.splitTextToSize(cleanText(cellText), colWidths[c] - 2 * tablePad);
            doc.text(cellLines[0] || '', xPos + tablePad, yPos);
            xPos += colWidths[c];
          }
          yPos += tableRowH;

          // Ligne sous header
          doc.setDrawColor(...COLORS.tableBorder);
          doc.setLineWidth(0.3);
          doc.line(CONFIG.marginLeft, yPos - 3, CONFIG.marginLeft + contentWidth, yPos - 3);
        }

        // Lignes du tableau
        doc.setFontSize(tableFontSize);
        doc.setFont('helvetica', 'normal');
        for (let r = 0; r < rows.length; r++) {
          yPos = checkPageBreak(doc, yPos, tableRowH);

          // Fond alterné
          if (r % 2 === 1) {
            doc.setFillColor(...COLORS.altRowBg);
            doc.rect(CONFIG.marginLeft, yPos - 4, contentWidth, tableRowH, 'F');
          }

          doc.setTextColor(...COLORS.textDark);
          xPos = CONFIG.marginLeft;
          for (let c = 0; c < numCols; c++) {
            const cellText = rows[r][c] || '';
            const cellLines = doc.splitTextToSize(cleanText(cellText), colWidths[c] - 2 * tablePad);
            doc.text(cellLines[0] || '', xPos + tablePad, yPos);
            xPos += colWidths[c];
          }
          yPos += tableRowH;
        }

        // Bordure basse du tableau
        doc.setDrawColor(...COLORS.tableBorder);
        doc.setLineWidth(0.2);
        doc.line(CONFIG.marginLeft, yPos - 3, CONFIG.marginLeft + contentWidth, yPos - 3);
        yPos += CONFIG.paragraphSpacing;
        break;
      }

      case 'callout': {
        const calloutColors: Record<string, [number, number, number]> = {
          warning: COLORS.warning,
          success: COLORS.success,
          error: COLORS.error,
          info: COLORS.textMedium,
        };
        const color = calloutColors[block.style] || COLORS.textMedium;
        const textLines = doc.splitTextToSize(cleanText(block.text), contentWidth - 8);
        const blockHeight = textLines.length * CONFIG.lineHeight + 6;
        yPos = checkPageBreak(doc, yPos, blockHeight);

        // Fond léger (couleur diluée manuellement — pas d'opacité en jsPDF standard)
        const bgR = Math.round(255 - (255 - color[0]) * 0.08);
        const bgG = Math.round(255 - (255 - color[1]) * 0.08);
        const bgB = Math.round(255 - (255 - color[2]) * 0.08);
        doc.setFillColor(bgR, bgG, bgB);
        doc.rect(CONFIG.marginLeft, yPos - 4, contentWidth, blockHeight, 'F');

        // Barre latérale
        doc.setFillColor(...color);
        doc.rect(CONFIG.marginLeft, yPos - 4, 1.5, blockHeight, 'F');

        // Texte
        doc.setFontSize(CONFIG.fontSizeNormal);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...color);
        for (const tl of textLines) {
          doc.text(tl, CONFIG.marginLeft + 5, yPos);
          yPos += CONFIG.lineHeight;
        }
        yPos += CONFIG.paragraphSpacing;
        doc.setTextColor(...COLORS.textDark);
        break;
      }

      case 'separator': {
        yPos = checkPageBreak(doc, yPos, 6);
        doc.setDrawColor(...COLORS.tableBorder);
        doc.setLineWidth(0.2);
        doc.line(CONFIG.marginLeft + 20, yPos, CONFIG.pageWidth - CONFIG.marginRight - 20, yPos);
        yPos += CONFIG.paragraphSpacing;
        break;
      }
    }
  }

  // ================================================================
  // PIED DE PAGE — toutes les pages
  // ================================================================

  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);

    // Bandeau ambre bas
    doc.setFillColor(...COLORS.primary);
    doc.rect(0, CONFIG.pageHeight - 3, CONFIG.pageWidth, 3, 'F');

    // Texte pied de page
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.textLight);

    const footerLeft = 'Document genere par SENTINELLE - Copro Protect - copro-protect.fr';
    doc.text(footerLeft, CONFIG.marginLeft, CONFIG.pageHeight - 6);

    const footerRight = `Page ${p}/${totalPages}`;
    const frWidth = doc.getTextWidth(footerRight);
    doc.text(footerRight, CONFIG.pageWidth - CONFIG.marginRight - frWidth, CONFIG.pageHeight - 6);

    // Mini-header pages suivantes
    if (p > 1) {
      doc.setFontSize(CONFIG.fontSizeSmall);
      doc.setTextColor(...COLORS.textLight);
      doc.text(cleanText(titrePDF), CONFIG.marginLeft, 10);
      const dateSmall = dateStr;
      const dsWidth = doc.getTextWidth(dateSmall);
      doc.text(dateSmall, CONFIG.pageWidth - CONFIG.marginRight - dsWidth, 10);
      doc.setDrawColor(...COLORS.tableBorder);
      doc.setLineWidth(0.2);
      doc.line(CONFIG.marginLeft, 12, CONFIG.pageWidth - CONFIG.marginRight, 12);
    }
  }

  return doc.output('blob');
}

// ============================================================================
// UTILITAIRES TABLEAU
// ============================================================================

/**
 * Calcule les largeurs de colonnes proportionnelles au contenu
 */
function calculateColumnWidths(
  doc: jsPDF,
  headers: string[],
  rows: string[][],
  totalWidth: number,
  numCols: number,
  fontSize?: number
): number[] {
  doc.setFontSize(fontSize || CONFIG.fontSizeSmall);

  // Mesurer la largeur max de chaque colonne
  const maxWidths: number[] = new Array(numCols).fill(0);
  const pad = fontSize && fontSize <= 7 ? 2 : CONFIG.tablePadding;
  for (let c = 0; c < numCols; c++) {
    if (headers[c]) {
      maxWidths[c] = Math.max(maxWidths[c], doc.getTextWidth(cleanText(headers[c])));
    }
    for (const row of rows) {
      if (row[c]) {
        maxWidths[c] = Math.max(maxWidths[c], doc.getTextWidth(cleanText(row[c])));
      }
    }
    maxWidths[c] += 2 * pad;
  }

  const totalNatural = maxWidths.reduce((s, w) => s + w, 0);

  if (totalNatural <= totalWidth) {
    // Tout rentre — distribuer l'espace restant proportionnellement
    const ratio = totalWidth / totalNatural;
    return maxWidths.map(w => w * ratio);
  }

  // Trop large — comprimer proportionnellement
  return maxWidths.map(w => (w / totalNatural) * totalWidth);
}

// ============================================================================
// FONCTIONS PUBLIQUES
// ============================================================================

/**
 * Télécharge le PDF d'un résultat SENTINELLE
 */
export function telechargerPDFSentinelle(options: PDFSentinelleOptions): void {
  const blob = genererPDFSentinelle(options);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const dateStr = options.dateMessage.toISOString().split('T')[0];
  const titreClean = (options.titre || extraireTitre(options.contenu))
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 40);
  link.download = `SENTINELLE_${dateStr}_${titreClean}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Ouvre le PDF dans un nouvel onglet
 */
export function ouvrirPDFSentinelle(options: PDFSentinelleOptions): void {
  const blob = genererPDFSentinelle(options);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}
